/**
 * PhonePreview — pinned 390×844 phone-frame iframe for widget previews.
 *
 * Why this lives in its own file (Issue: P0 regression on round-2 commit
 * 2541a72d1): when this component was defined as a top-level function
 * inside WidgetEditor.jsx, Vite's React Fast Refresh occasionally lost
 * the binding during HMR cycles — the parent JSX rendered while the
 * symbol was momentarily out of scope, throwing
 *   `ReferenceError: PhonePreview is not defined`
 * caught by the ErrorBoundary and reflowing the layout (chat panel fell
 * below the phone). Putting the component in its own module gives Fast
 * Refresh a stable module boundary and keeps the parent's exports
 * eligible for refresh-as-component rather than refresh-as-module.
 *
 * Contract (props):
 *   previewUrl : string | null
 *     URL passed to the iframe `src`. When null, renders a "no dev
 *     version yet" placeholder.
 *   previewRef : RefObject<HTMLIFrameElement> | undefined
 *     Optional ref the parent uses to imperatively reload the iframe.
 *   colors     : { textDim, bgPanel, border }
 *     Theme tokens for the caption strip. Required so this module
 *     does not need to re-import the parent's COLORS map.
 *
 * Layout:
 *   - Outer wrapper: flex-1, 200px min-height, ResizeObserver-driven
 *     uniform scale via CSS transform so the inner iframe viewport
 *     remains 390px (matches iPhone-13 logical viewport).
 *   - Phone chrome: 14px black bezel, 48px corner radius, white face.
 *   - Caption strip: tiny "Phone preview · 390×844 · NN%" under the
 *     frame. Uses `colors` so it matches the parent palette.
 */
import { useEffect, useRef, useState } from 'react';

const PHONE_W = 390;
const PHONE_H = 844;
const PHONE_BORDER = 14;
const PHONE_FRAME_W = PHONE_W + PHONE_BORDER * 2;
const PHONE_FRAME_H = PHONE_H + PHONE_BORDER * 2;
const PHONE_PADDING = 24;

// Tile mode dims — mirror WidgetCatalog's THUMB_W / THUMB_H. When the
// caller passes `tileSize`, the iframe renders at this fixed footprint
// centered inside the phone face with a homescreen-ish backdrop, so the
// designer can confirm cover.html looks right at the size it actually
// ships in the iPhone widget grid. Default lives here (not in the
// parents) so both Editor + UseDashboard get the same answer.
const DEFAULT_TILE_W = 240;
const DEFAULT_TILE_H = 170;

export default function PhonePreview({ previewUrl, previewRef, colors, children, showCaption = true, padding, tileSize, iconUrl, iconOnly = false }) {
  const wrapRef = useRef(null);
  const [scale, setScale] = useState(1);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const pad = typeof padding === 'number' ? padding : PHONE_PADDING;
    const ro = new ResizeObserver(entries => {
      for (const entry of entries) {
        const availW = entry.contentRect.width - pad * 2;
        const availH = entry.contentRect.height - pad * 2;
        if (availW <= 0 || availH <= 0) continue;
        const sW = availW / PHONE_FRAME_W;
        const sH = availH / PHONE_FRAME_H;
        const next = Math.min(1, sW, sH);
        setScale(next > 0 ? next : 1);
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [padding]);

  // Light fallback palette — matches PlaygroundView `V` tokens. Parent
  // passes its own COLORS map, but we default to light so HMR / isolation
  // tests don't render dark chrome on the otherwise-light playground.
  const c = colors || { textDim: '#6e6e73', bgPanel: '#ffffff', border: '#d8d8dc' };

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 200 }}>
      <div
        ref={wrapRef}
        style={{
          flex: 1,
          // Slightly darker than the panel so the phone bezel stands out
          // (the bezel stays black, the face stays white).
          background: '#eeeef0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: typeof padding === 'number' ? padding : PHONE_PADDING,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            width: PHONE_FRAME_W,
            height: PHONE_FRAME_H,
            border: `${PHONE_BORDER}px solid #000`,
            borderRadius: 48,
            background: '#FFFFFF',
            overflow: 'hidden',
            position: 'relative',
            boxSizing: 'border-box',
            transform: `scale(${scale})`,
            transformOrigin: 'center center',
            transition: 'transform 80ms linear',
            flex: '0 0 auto',
          }}
        >
          {children ? (
            // Consumer-supplied content (e.g. WidgetRuntime mounts the iframe
            // + overlay nav-pill + capture sheet here). PhonePreview just
            // owns the bezel + auto-scale.
            children
          ) : iconOnly ? (
            // Icon mode (studio dropdown "Icon"): show the widget mark alone,
            // home-screen-app-icon style, centered on a homescreen backdrop.
            <div style={{
              width: '100%', height: '100%',
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12,
              background: 'linear-gradient(160deg,#f4f4f7 0%,#dcdce3 100%)',
            }}>
              <div style={{
                width: 120, height: 120, borderRadius: 28, background: '#FFFFFF',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 10px 28px rgba(0,0,0,0.20), 0 1px 2px rgba(0,0,0,0.12)',
                overflow: 'hidden',
              }}>
                <img
                  src={iconUrl || '/default-widget-icon.svg'}
                  alt="widget icon"
                  style={{ width: 100, height: 100, objectFit: 'contain', display: 'block' }}
                />
              </div>
              <div style={{
                fontSize: 10, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#8a8a90',
              }}>Icon</div>
            </div>
          ) : previewUrl ? (
            tileSize ? (
              // Tile mode: render cover.html at its native catalog-tile size
              // (240×170 by default) centered inside the phone face, on a
              // homescreen-ish gradient backdrop so the designer can confirm
              // the cover reads correctly at the size users actually see.
              // When `iconUrl` is supplied (studio "Cover" view), split the
              // face 50/50 — the widget's icon on the top half, cover.html on
              // the bottom half — so the designer visualizes BOTH at once.
              <div style={{
                width: '100%', height: '100%',
                display: 'flex', flexDirection: 'column', alignItems: 'stretch',
                background: 'linear-gradient(160deg,#f4f4f7 0%,#dcdce3 100%)',
              }}>
                {iconUrl && (
                  <div style={{
                    flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column',
                    alignItems: 'center', justifyContent: 'center', gap: 6, padding: 10,
                  }}>
                    <div style={{
                      width: 96, height: 96, borderRadius: 22, background: '#FFFFFF',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      boxShadow: '0 6px 18px rgba(0,0,0,0.18), 0 1px 2px rgba(0,0,0,0.10)',
                      overflow: 'hidden',
                    }}>
                      <img
                        src={iconUrl}
                        alt="widget icon"
                        style={{ width: 80, height: 80, objectFit: 'contain', display: 'block' }}
                      />
                    </div>
                    <div style={{
                      fontSize: 10, letterSpacing: '0.06em', textTransform: 'uppercase',
                      color: '#8a8a90',
                    }}>Icon</div>
                  </div>
                )}
                <div style={{
                  flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column',
                  alignItems: 'center', justifyContent: 'center', gap: 6, padding: 10,
                }}>
                  <div style={{
                    width: tileSize.w || DEFAULT_TILE_W,
                    height: tileSize.h || DEFAULT_TILE_H,
                    maxWidth: '100%',
                    borderRadius: 18,
                    overflow: 'hidden',
                    boxShadow: '0 6px 18px rgba(0,0,0,0.18), 0 1px 2px rgba(0,0,0,0.10)',
                    background: '#F2F2F7',
                    position: 'relative',
                  }}>
                    <iframe
                      ref={previewRef}
                      src={previewUrl}
                      title="widget-cover-preview"
                      sandbox="allow-scripts"
                      scrolling="no"
                      style={{
                        width: '100%', height: '100%', border: 0, display: 'block',
                        pointerEvents: 'none',
                      }}
                    />
                  </div>
                  {iconUrl && (
                    <div style={{
                      fontSize: 10, letterSpacing: '0.06em', textTransform: 'uppercase',
                      color: '#8a8a90',
                    }}>Cover</div>
                  )}
                </div>
              </div>
            ) : (
            <iframe
              ref={previewRef}
              src={previewUrl}
              title="widget-preview"
              // `allow-scripts` only (no `allow-same-origin`). Chrome
              // warns when both flags are set together ("iframe can
              // escape its sandboxing"). Dropping the same-origin flag
              // matches the policy: widgets must not store user data in
              // localStorage (designer system prompt enforces this) — the
              // canonical data feed is `window.__WIDGET_DATA__` injected
              // by the host. Widgets that ignore the rule and read
              // localStorage will silently fail; that's a widget bug,
              // not a host bug.
              sandbox="allow-scripts"
              style={{
                width: '100%',
                height: '100%',
                border: 0,
                display: 'block',
              }}
            />
            )
          ) : (
            <div style={{
              padding: 24, color: '#666', fontSize: 13,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              width: '100%', height: '100%', textAlign: 'center',
            }}>
              Edit a file to see it here. The preview tracks your autosaves live; Commit when you're ready to snapshot a version.
            </div>
          )}
        </div>
      </div>
      {showCaption && (
        <div style={{
          padding: '4px 12px', fontSize: 10, color: c.textDim,
          textAlign: 'center', letterSpacing: '0.04em', textTransform: 'uppercase',
          background: c.bgPanel, borderTop: `1px solid ${c.border}`,
        }}>
          Phone preview · {PHONE_W}×{PHONE_H}{scale < 1 ? ` · ${Math.round(scale * 100)}%` : ''}
        </div>
      )}
    </div>
  );
}
